import { Intent } from '@Components/designConstants';
import { ErrorSource, ModalError } from '../redux/modals-types';
import { hashString } from './colors';
import { reportError, resolveErrorMessage } from './errorReporting';
import { findFetchError } from './fetch-error';
import { UntraceableError } from './untraceable-error';

export interface ErrorReference {
  source: ErrorSource;
  id?: string;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const URL_RE = /https?:\/\/[^\s"'`)]+/gi;
// Bundler output names: assets/<Name>-<8-char content hash>.js. The hash changes every deploy.
// The lookahead requires a digit or uppercase letter so an unhashed 8-letter suffix
// (assets/app-settings.js) is not mistaken for a hash.
const ASSET_HASH_RE = /(assets\/[\w.-]+?)-(?=[\w-]{0,7}[\dA-Z])[\w-]{8}(\.(?:m?js|css))/g;
const LONG_TOKEN_RE = /\b[0-9a-f]{16,}\b/gi;
const DIGITS_RE = /\d+/g;
const MAX_NORMALIZED_LENGTH = 200;

// Strip the parts of an error message that vary per user/request (ids, numbers, query strings)
// while keeping the parts that identify the bug (property names, identifiers), so the same
// functional bug fingerprints identically across sessions. Quoted text is kept deliberately:
// "reading 'name'" vs "reading 'id'" are different bugs and must not collapse together.
export const normalizeErrorMessage = (message: string): string =>
  message
    .replace(URL_RE, url => url.split('?')[0])
    .replace(ASSET_HASH_RE, '$1-<hash>$2')
    .replace(UUID_RE, '<uuid>')
    .replace(LONG_TOKEN_RE, '<token>')
    .replace(DIGITS_RE, '#')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_NORMALIZED_LENGTH);

// Hash of error name + normalized message (no stack frames).
export const frontendErrorId = (error: unknown): string => {
  const name = error instanceof Error ? error.name : typeof error;
  const message = resolveErrorMessage(error);
  const fingerprint = hashString(`${name}|${normalizeErrorMessage(message)}`) % 0x100000000;
  return `FE-${fingerprint.toString(16).padStart(8, '0')}`;
};

// Statuses a gateway/load balancer generates itself, before any logged code runs.
const GATEWAY_STATUSES = new Set([502, 503, 504]);

// The single rule for which error dialogs carry a reference ID:
//   backend  -> a Lambda failed and returned its requestId: show it (findable in Datadog Logs)
//   frontend -> an app bug (not an API failure): show a stable FE- fingerprint (findable in RUM)
//   anything else (validation text, gateway/transport failures, non-error intents) -> no ID
export const classifyModalError = (
  error: Pick<ModalError, 'intent' | 'reportError' | 'errorSource' | 'referenceId'>
): ErrorReference => {
  if ((error.intent ?? Intent.ERROR) !== Intent.ERROR) {
    return { source: 'none' };
  }
  if (error.errorSource) {
    return { source: error.errorSource, id: error.referenceId };
  }
  const { reportError: thrown } = error;
  // A wrapped API failure (`new Error(context, { cause: fetchError })`) is still the backend's error.
  const fetchError = findFetchError(thrown);
  if (fetchError) {
    if (fetchError.requestId) {
      return { source: 'backend', id: fetchError.requestId };
    }
    return GATEWAY_STATUSES.has(fetchError.status) ? { source: 'gateway' } : { source: 'none' };
  }
  if (thrown instanceof UntraceableError) {
    return { source: thrown.kind };
  }
  // A non-Error object stringifies to '[object Object]', which would give every one the same ID.
  if (thrown && resolveErrorMessage(thrown) !== '[object Object]') {
    return { source: 'frontend', id: frontendErrorId(thrown) };
  }
  return { source: 'none' };
};

const REFERENCE_LABEL: Record<'backend' | 'frontend', string> = {
  backend: 'Reference ID',
  frontend: 'Frontend error ID'
};

// Label shown next to a reference ID, by where the ID can be searched.
export const referenceLabel = (source: ErrorSource): string =>
  REFERENCE_LABEL[source as keyof typeof REFERENCE_LABEL] ?? REFERENCE_LABEL.backend;

// Dialog note for sources that carry no reference ID.
export const NO_ID_SOURCE_NOTE: Partial<Record<ErrorSource, string>> = {
  gateway:
    "This request failed before it reached code that could log it (e.g. a gateway timeout), so there's no reference ID.",
  network: 'This request never reached our servers, so there is no reference ID.',
  timeout:
    'The page stopped waiting for the job, which may still be running, so there is no reference ID.',
  job: 'The background job failed, so there is no reference ID.'
};

// RUM context for a classified error: the backend requestId (searchable in Datadog Logs) or the FE-
// errorId (searchable in RUM as @context.errorId), always with the source, so a dialog's ID and its
// RUM event can be matched.
export const referenceExtra = ({ source, id }: ErrorReference): Record<string, unknown> => ({
  errorSource: source,
  ...(source === 'backend' && id ? { requestId: id } : {}),
  ...(source === 'frontend' && id ? { errorId: id } : {})
});

// Classify a raw error, report it to RUM/Mixpanel tagged with that classification, and hand back
// the classification so the caller can show the exact same ID in the UI. For paths with no
// ModalError of their own (the ErrorBoundary and the window-level handlers).
export const reportWithReference = (error: unknown, operation: string): ErrorReference => {
  const reference = classifyModalError({ reportError: error });
  reportError(error, {
    operation,
    fingerprint: reference.source === 'frontend' ? reference.id : undefined,
    extra: referenceExtra(reference)
  });
  return reference;
};
